/* eslint-disable max-lines-per-function */
/**
 * Event Handlers Utility
 * ============================================================================
 *
 * Purpose:
 * --------
 * Pre-configured event handlers for business events.
 * Integrates real-time events with notification system and data invalidation.
 *
 * This utility provides:
 * - Event handler registration
 * - Integration with notification system
 * - Integration with React Query cache invalidation
 * - Event logging
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** approach
 * - Easy integration with existing layers
 * - Composable event handlers
 * - Type-safe event handling
 *
 * Usage:
 * ------
 * ```typescript
 * import { registerBusinessEventHandlers } from 'core/realtime';
 *
 * // Register all business event handlers
 * registerBusinessEventHandlers();
 * ```
 * ============================================================================
 */

import { BUSINESS_EVENT, EVENT_NOTIFICATION_DURATION, EVENT_NOTIFICATION_KEYS } from '../constants';
import type { EventPayload } from '../types';
import { showNotification } from '../../notifications';
import type { NotificationType } from '../../notifications/types';

/** Payload data with optional entity ID */
type EntityData = { id?: string };

/** Extend Window interface for global queryClient */
declare global {
  interface Window {
    queryClient?: {
      invalidateQueries: (options: { queryKey: string[] }) => void;
    };
  }
}

/**
 * Register Business Event Handlers
 *
 * Registers handlers for all business events that show notifications
 * and invalidate cached data.
 *
 * @param socketService - Socket service instance (for subscribing to events)
 * @param queryClient - React Query client (optional, for cache invalidation)
 */
export const registerBusinessEventHandlers = (
  socketService: { on: (event: string, handler: (payload: EventPayload) => void) => () => void },
  queryClient?: any
): (() => void) => {
  const unsubscribeFunctions: Array<() => void> = [];

  // Policy Events
  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.POLICY_CREATED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.POLICY_CREATED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.POLICY_CREATED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate policies cache
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['policies'] });
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.POLICY_UPDATED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.POLICY_UPDATED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.POLICY_UPDATED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate policies cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['policies'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['policy', data.id] });
        }
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.POLICY_DELETED, (payload: EventPayload) => {
      showNotification('warning', EVENT_NOTIFICATION_KEYS.POLICY_DELETED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.POLICY_DELETED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate policies cache
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['policies'] });
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.POLICY_APPROVED, (payload: EventPayload) => {
      showNotification('success', EVENT_NOTIFICATION_KEYS.POLICY_APPROVED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.POLICY_APPROVED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate policies cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['policies'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['policy', data.id] });
        }
      }
    })
  );

  // Claim Events
  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CLAIM_CREATED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.CLAIM_CREATED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CLAIM_CREATED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate claims cache
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['claims'] });
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CLAIM_UPDATED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.CLAIM_UPDATED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CLAIM_UPDATED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate claims cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['claims'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['claim', data.id] });
        }
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CLAIM_APPROVED, (payload: EventPayload) => {
      showNotification('success', EVENT_NOTIFICATION_KEYS.CLAIM_APPROVED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CLAIM_APPROVED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate claims cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['claims'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['claim', data.id] });
        }
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CLAIM_REJECTED, (payload: EventPayload) => {
      showNotification('error', EVENT_NOTIFICATION_KEYS.CLAIM_REJECTED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CLAIM_REJECTED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate claims cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['claims'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['claim', data.id] });
        }
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CLAIM_STATUS_CHANGED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.CLAIM_STATUS_CHANGED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CLAIM_STATUS_CHANGED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate claims cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['claims'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['claim', data.id] });
        }
      }
    })
  );

  // Customer Events
  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CUSTOMER_CREATED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.CUSTOMER_CREATED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CUSTOMER_CREATED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate customers cache
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      }
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.CUSTOMER_UPDATED, (payload: EventPayload) => {
      showNotification('info', EVENT_NOTIFICATION_KEYS.CUSTOMER_UPDATED.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.CUSTOMER_UPDATED.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
      });

      // Invalidate customers cache
      if (queryClient) {
        const data = payload.data as EntityData | undefined;
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        if (data?.id) {
          queryClient.invalidateQueries({ queryKey: ['customer', data.id] });
        }
      }
    })
  );

  // Notification Events
  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.NOTIFICATION, (payload: EventPayload) => {
      const data = payload.data as
        | { type?: NotificationType; title?: string; message?: string }
        | undefined;

      showNotification(
        data?.type ?? 'info',
        data?.message ?? EVENT_NOTIFICATION_KEYS.NOTIFICATION.MESSAGE,
        {
          title: data?.title ?? EVENT_NOTIFICATION_KEYS.NOTIFICATION.TITLE,
          duration: EVENT_NOTIFICATION_DURATION.DEFAULT,
        }
      );
    })
  );

  // System Events
  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.SYSTEM_ALERT, (payload: EventPayload) => {
      const data = payload.data as { message?: string; severity?: NotificationType } | undefined;

      showNotification(
        data?.severity ?? 'warning',
        data?.message ?? EVENT_NOTIFICATION_KEYS.SYSTEM_ALERT.MESSAGE,
        {
          title: EVENT_NOTIFICATION_KEYS.SYSTEM_ALERT.TITLE,
          duration: EVENT_NOTIFICATION_DURATION.SYSTEM_ALERT,
        }
      );
    })
  );

  unsubscribeFunctions.push(
    socketService.on(BUSINESS_EVENT.SYSTEM_MAINTENANCE, (payload: EventPayload) => {
      showNotification('warning', EVENT_NOTIFICATION_KEYS.SYSTEM_MAINTENANCE.MESSAGE, {
        title: EVENT_NOTIFICATION_KEYS.SYSTEM_MAINTENANCE.TITLE,
        duration: EVENT_NOTIFICATION_DURATION.SYSTEM_MAINTENANCE,
      });
    })
  );

  // Return cleanup function
  return () => {
    unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
  };
};

/**
 * Create Custom Event Handler
 *
 * Helper to create a custom event handler with notification and cache invalidation.
 *
 * @param event - Event name
 * @param options - Handler options
 * @returns Event handler function
 */
export const createEventHandler = (
  event: string,
  options: {
    notification?: {
      type: 'success' | 'error' | 'warning' | 'info';
      title: string;
      message: string;
      duration?: number;
    };
    invalidateQueries?: string[][];
    onEvent?: (payload: EventPayload) => void;
  }
) => {
  return (payload: EventPayload) => {
    // Show notification
    if (options.notification) {
      showNotification(options.notification.type, options.notification.message, {
        title: options.notification.title,
        duration: options.notification.duration,
      });
    }

    // Invalidate queries (React Query cache)
    const queryClient = window.queryClient;
    if (options.invalidateQueries && queryClient) {
      options.invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }

    // Custom handler
    if (options.onEvent) {
      options.onEvent(payload);
    }
  };
};
